import { resolveSiteStudyArea } from '../siteStudyArea';
import type {
  GenerationImpactScenarioSnapshot,
  GenerationImpactSedAuthoritySnapshot,
  TestFitAuthorityRef,
} from '../../types/generationImpact';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { isStaleSed } from '../sedStaleness';
import {
  createGenerationImpactParcelBoundarySnapshot,
  sameGenerationImpactParcelBoundary,
} from './boundaryAuthority';
import { sameGenerationImpactLayoutHeadAndPublication } from './integrity';
import { resolveGenerationImpactSedAuthority } from './sedAuthority';

export type TestFitAuthoritySite = Pick<
  SiteEntity,
  | 'projectIntent'
  | 'generationAssessmentSelection'
  | 'promotedGenerationScenarioSnapshot'
  | 'generationImpactLayoutHead'
  | 'studyArea'
  | 'siteLayers'
  | 'siteBoundaries'
  | 'siteBoundary'
  | 'sedId'
  | 'sedVersion'
  | 'sedStatus'
  | 'sedComputedAt'
>;

export interface TestFitSedIdentity {
  sedId: string;
  sedVersion: number;
}

function sameScenarioRevision(
  left: GenerationImpactScenarioSnapshot | null | undefined,
  right: GenerationImpactScenarioSnapshot | null | undefined,
): boolean {
  return Boolean(
    left
      && right
      && left.id === right.id
      && left.name === right.name
      && left.updatedAt === right.updatedAt
      && left.inputHash === right.inputHash
      && left.resultHash === right.resultHash
      && left.result.modelVersion === right.result.modelVersion
      && (left.promotedAt ?? undefined) === (right.promotedAt ?? undefined)
      && (left.promotedBy ?? undefined) === (right.promotedBy ?? undefined),
  );
}

/**
 * Project a structurally valid frozen SED authority into the compact identity
 * carried by Test Fit jobs and reports.
 */
export function createTestFitAuthorityRefFromAuthority(
  authority: GenerationImpactSedAuthoritySnapshot,
  sed: TestFitSedIdentity,
): TestFitAuthorityRef {
  const resolved = resolveGenerationImpactSedAuthority({
    generationImpactAuthoritySnapshot: authority,
  });
  if (resolved.status !== 'valid') {
    throw new Error(
      resolved.status === 'invalid'
        ? `Cannot create Test Fit authority: ${resolved.reason}`
        : 'Cannot create Test Fit authority: the SED authority is absent',
    );
  }
  if (typeof sed.sedId !== 'string' || sed.sedId.length === 0) {
    throw new Error('Cannot create Test Fit authority without a SED id');
  }
  if (!Number.isInteger(sed.sedVersion) || sed.sedVersion < 1) {
    throw new Error('Cannot create Test Fit authority without a positive SED version');
  }

  const snapshot = resolved.snapshot;
  const scenario = snapshot.scenarioSnapshot;
  const provenance = snapshot.provenance;
  if (!scenario.promotedAt) {
    throw new Error('Cannot create Test Fit authority from an unpromoted generation scenario');
  }

  return Object.freeze({
    scenarioId: scenario.id,
    scenarioUpdatedAt: scenario.updatedAt,
    inputHash: provenance.inputHash,
    resultHash: provenance.resultHash,
    modelVersion: provenance.modelVersion,
    promotedAt: scenario.promotedAt,
    layoutPublicationId: provenance.layoutPublicationId,
    layoutVersion: provenance.layoutVersion,
    layoutHash: provenance.layoutHash,
    layoutPublishedAt: provenance.layoutPublishedAt,
    sedId: sed.sedId,
    sedVersion: sed.sedVersion,
    boundaryHash: provenance.parcelBoundaryHash,
  });
}

/**
 * Resolve a Test Fit authority only when the SED, active site pointers,
 * promoted scenario, published layout, and current parcel boundary all match.
 * Returns null for any incomplete/stale authority; `require…` below exposes a
 * fail-loud variant for generation backstops.
 */
export function createTestFitAuthorityRef(
  site: TestFitAuthoritySite,
  sed: SiteEvidenceDocument | null | undefined,
): TestFitAuthorityRef | null {
  if (
    site.generationAssessmentSelection?.kind === 'grid-led-standby-planning'
    || !sed
    || site.sedStatus !== 'complete'
    || !site.sedId
    || site.sedId !== sed.id
    || site.sedVersion == null
    || site.sedVersion !== sed.version
    || isStaleSed(site)
  ) {
    return null;
  }
  const resolved = resolveGenerationImpactSedAuthority(sed);
  if (resolved.status !== 'valid') return null;

  const authority = resolved.snapshot;
  const promoted = site.promotedGenerationScenarioSnapshot;
  const intent = site.projectIntent;
  const head = site.generationImpactLayoutHead;
  if (
    !promoted
    || !promoted.promotedAt
    || !sameScenarioRevision(promoted, authority.scenarioSnapshot)
    || intent?.promotedGenerationScenarioId !== promoted.id
    || intent.promotedGenerationInputHash !== promoted.inputHash
    || intent.promotedGenerationResultHash !== promoted.resultHash
    || intent.promotedGenerationPromotedAt !== promoted.promotedAt
    || !sameGenerationImpactLayoutHeadAndPublication(head, authority.layoutPublication)
  ) {
    return null;
  }

  try {
    if ((resolveSiteStudyArea(site)?.scope.selectionHash ?? null) !== (sed.studyAreaScope?.selectionHash ?? null)) return null;
    const currentBoundary = createGenerationImpactParcelBoundarySnapshot(site);
    if (!sameGenerationImpactParcelBoundary(authority.parcelBoundary, currentBoundary)) return null;
  } catch {
    return null;
  }

  return createTestFitAuthorityRefFromAuthority(authority, {
    sedId: sed.id,
    sedVersion: sed.version,
  });
}

export function requireTestFitAuthorityRef(
  site: TestFitAuthoritySite,
  sed: SiteEvidenceDocument | null | undefined,
): TestFitAuthorityRef {
  const authority = createTestFitAuthorityRef(site, sed);
  if (!authority) {
    throw new Error(
      'Test Fit authority is missing or stale relative to the promoted scenario, published layout, SED, or parcel boundary',
    );
  }
  return authority;
}

const AUTHORITY_FIELDS = [
  'scenarioId',
  'scenarioUpdatedAt',
  'inputHash',
  'resultHash',
  'modelVersion',
  'promotedAt',
  'layoutPublicationId',
  'layoutVersion',
  'layoutHash',
  'layoutPublishedAt',
  'sedId',
  'sedVersion',
  'boundaryHash',
] as const satisfies readonly (keyof TestFitAuthorityRef)[];

export function sameTestFitAuthorityRef(
  left: TestFitAuthorityRef | null | undefined,
  right: TestFitAuthorityRef | null | undefined,
): boolean {
  return Boolean(
    left
      && right
      && AUTHORITY_FIELDS.every(field => left[field] === right[field]),
  );
}

const STALENESS_LABEL: Readonly<Record<keyof TestFitAuthorityRef, string>> = {
  scenarioId: 'promoted scenario changed',
  scenarioUpdatedAt: 'saved scenario revision changed',
  inputHash: 'scenario inputs changed',
  resultHash: 'scenario results changed',
  modelVersion: 'generation model version changed',
  promotedAt: 'scenario promotion changed',
  layoutPublicationId: 'layout publication changed',
  layoutVersion: 'layout version changed',
  layoutHash: 'published layout geometry changed',
  layoutPublishedAt: 'layout publication timestamp changed',
  sedId: 'Site Evidence Document changed',
  sedVersion: 'Site Evidence Document version changed',
  boundaryHash: 'parcel boundary changed',
};

/**
 * Return a stable, user-readable reason when a report/job authority no longer
 * matches current site authority. Null means every identity field matches.
 */
export function testFitAuthorityStalenessReason(
  expected: TestFitAuthorityRef | null | undefined,
  current: TestFitAuthorityRef | null | undefined,
): string | null {
  if (!expected) return 'Test Fit has no frozen Generation & Impact authority';
  if (!current) return 'Current Generation & Impact authority is incomplete or unavailable';
  for (const field of AUTHORITY_FIELDS) {
    if (expected[field] !== current[field]) return STALENESS_LABEL[field];
  }
  return null;
}

export const testFitAuthorityInternals = {
  sameScenarioRevision,
  authorityFields: AUTHORITY_FIELDS,
};
